/**
 * Lightweight heartbeat hook — writes `lastActivityAt: serverTimestamp()` to
 * `clubs/{clubId}` whenever both a club is selected and a real club member is
 * signed in. Debounced to at most once per 15 minutes per club so we don't
 * hammer Firestore.
 *
 * Debounce state lives in TWO places:
 *   1. In-memory: fastest check — avoids any async overhead on every render.
 *   2. localStorage (key `cm.lastActivity.{clubId}`): survives page reloads so
 *      a hard-refresh doesn't trigger an immediate write.
 *
 * Admin-skip: when the current user is a platform admin AND has no staff entry
 * on the current club (`staffRole === null`), the write is suppressed. This
 * matches the impersonation scenario where an admin sets `currentClubId` to a
 * club they don't belong to.
 */

import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/core/auth/auth-store'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import { useIsPlatformAdmin } from '@/core/auth/use-is-admin'
import { useCurrentRole } from '@/features/staff/hooks/use-current-role'
import { usePreviewSession } from '@/features/preview-session/hooks/use-preview-session'
import { adminRepository } from '../api/admin-repository'
import { shouldWriteActivity } from '../domain/last-activity'
import { logger } from '@/core/logger'

const STORAGE_KEY_PREFIX = 'cm.lastActivity.'

function storageKey(clubId: string): string {
  return `${STORAGE_KEY_PREFIX}${clubId}`
}

function readStoredTimestamp(clubId: string): number | null {
  try {
    const raw = localStorage.getItem(storageKey(clubId))
    if (raw == null) return null
    const parsed = parseInt(raw, 10)
    return isNaN(parsed) ? null : parsed
  } catch {
    return null
  }
}

function storeTimestamp(clubId: string, ts: number): void {
  try {
    localStorage.setItem(storageKey(clubId), String(ts))
  } catch {
    // Storage quota exceeded or private-browsing restriction — silently skip.
  }
}

export function useTrackClubActivity(): void {
  const user = useAuthStore((s) => s.user)
  const clubId = useCurrentClubStore((s) => s.clubId)
  const { isAdmin, isLoading: adminLoading } = useIsPlatformAdmin()
  const { staffRole, isLoading: roleLoading } = useCurrentRole()
  const previewSession = usePreviewSession()

  // In-memory last-write timestamp, keyed by clubId to handle club switches.
  const lastWriteRef = useRef<Record<string, number>>({})

  useEffect(() => {
    // Wait until all async lookups have settled.
    if (adminLoading || roleLoading) return
    // Must have an authenticated user and a selected club.
    if (!user || !clubId) return

    // Admin-skip: platform admin with no real staff entry → impersonation mode.
    if (isAdmin && staffRole === null) return

    // Preview-session skip: visitors aren't real users — they have no staff
    // doc, so the lastActivityAt write would fail at the rules layer (and
    // even if it didn't, we don't want preview viewers to keep mockup
    // clubs "warm" in the activity feed).
    if (previewSession.active) return

    const now = new Date()
    const inMemory = lastWriteRef.current[clubId] ?? null
    // Prefer the more-recent of the two timestamps.
    const stored = readStoredTimestamp(clubId)
    const lastWrite = inMemory !== null && stored !== null
      ? Math.max(inMemory, stored)
      : inMemory ?? stored

    if (!shouldWriteActivity(lastWrite, now)) return

    const ts = now.getTime()
    lastWriteRef.current[clubId] = ts
    storeTimestamp(clubId, ts)

    adminRepository.touchLastActivity(clubId).catch((err) => {
      logger.w('useTrackClubActivity: write failed', err)
    })
  }, [user, clubId, isAdmin, staffRole, adminLoading, roleLoading, previewSession.active])
}
